import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import axios from 'axios'
import { AssessmentTemplate } from '../../types/assessments'
import { toast } from 'react-toastify'

interface CreateAssessmentModalProps {
  closeModal: () => void
  template: AssessmentTemplate | null
}

interface FormData {
  title: string
  subtitle: string
  description: string
  category: string
  icon: string
  color: string
}

interface FormErrors {
  title?: string
  category?: string
}

const icons = ['🛡️', '☁️', '🔒', '💻', '⚛️', '🟢', '🗄️', '🏗️']

const CATEGORIES = [
  'Cyber-Security Maturity',
  'Cloud Adoption Maturity',
  'Cloud Security Maturity',
  'AI Adoption Readiness',
  'Hypervisor → Hyperscaler Licensing',
  'Application Modernisation',
  'Managed Services Readiness',
  'Data Security Maturity'
]

const predefinedColors = [
  { name: 'Red', value: '#e74c3c' },
  { name: 'Blue', value: '#3498db' },
  { name: 'Purple', value: '#9b59b6' },
  { name: 'Green', value: '#27ae60' },
  { name: 'Orange', value: '#f39c12' },
  { name: 'Teal', value: '#1abc9c' },
  { name: 'Pink', value: '#e91e63' },
  { name: 'Indigo', value: '#3f51b5' }
]

export const CreateAssessmentModal: React.FC<CreateAssessmentModalProps> = ({
  closeModal,
  template
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    subtitle: '',
    description: '',
    category: '',
    icon: '',
    color: '#3498db'
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (template) {
      setFormData({
        title: template.title,
        subtitle: template.subtitle,
        description: template.description,
        category: template.category,
        icon: template.icon,
        color: template.color
      })
      setErrors({})
      setTouched({})
    } else {
      // Blank template - reset form
      setFormData({
        title: '',
        subtitle: '',
        description: '',
        category: '',
        icon: '',
        color: '#3498db'
      })
      setErrors({})
      setTouched({})
    }
  }, [template])

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.category) {
      newErrors.category = 'Category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, [field]: value }))
      // Clear error when user starts typing
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    }

  const handleBlur = (field: keyof FormData) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouched({
      title: true,
      category: true
    })

    if (!validate()) {
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('auth-token')
      const apiUrl = process.env.REACT_APP_RESUME_PARSER_URL || 'http://localhost:4000'

      const payload = {
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        description: formData.description || '',
        icon: formData.icon || '📝',
        color: formData.color || '#3498db',
        category: formData.category || '',
        totalMarks: null,
        passingMarks: null,
        durationMinutes: null,
        status: 'ACTIVE',
        questions: []
      }

      const response: any = await axios.post(`${apiUrl}/api/assessment/`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      console.log(response)
      if (response.status === 200 || response.status === 201) {
        toast.success('Assessment created successfully')
        closeModal()
      } else {
        toast.error('Failed to create assessment')
      }

      closeModal()
      // You might want to show a success message or refresh the list
    } catch (error: any) {
      console.error('Failed to create assessment:', error)
      toast.error(error.response.data.message || 'Failed to create assessment')
      // You might want to show an error message
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='card' style={{ maxWidth: '100%', margin: 0 }}>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <h3 className='card-title fw-bolder mb-0'>Create Assessment</h3>
        <button
          type='button'
          className='btn-close'
          onClick={closeModal}
          aria-label='Close'
        ></button>
      </div>
      <form className='form' onSubmit={handleSubmit}>
        <div
          className='card-body p-9'
          style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto' }}
        >
          {/* Basic Information Section */}
          <div className='mb-10'>
            <h4 className='fw-bold mb-4'>Basic Information</h4>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Title</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  value={formData.title}
                  onChange={handleChange('title')}
                  onBlur={handleBlur('title')}
                  className={clsx('form-control form-control-lg form-control-solid', {
                    'is-invalid': touched.title && errors.title,
                    'is-valid': touched.title && !errors.title
                  })}
                  placeholder='Enter assessment title'
                />
                {touched.title && errors.title && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert' className='text-danger'>
                        {errors.title}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>Subtitle</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  value={formData.subtitle}
                  onChange={handleChange('subtitle')}
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Enter assessment subtitle'
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>Description</label>
              <div className='col-lg-8 fv-row'>
                <textarea
                  value={formData.description}
                  onChange={handleChange('description')}
                  className='form-control form-control-lg form-control-solid'
                  rows={3}
                  placeholder='Enter assessment description'
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-semibold fs-6'>Category</label>
              <div className='col-lg-8 fv-row'>
                <select
                  value={formData.category}
                  onChange={handleChange('category')}
                  onBlur={handleBlur('category')}
                  className={clsx('form-select form-select-lg form-select-solid', {
                    'is-invalid': touched.category && errors.category,
                    'is-valid': touched.category && !errors.category
                  })}
                >
                  <option value=''>Select a category</option>
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {touched.category && errors.category && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert' className='text-danger'>
                        {errors.category}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>Icon</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  value={formData.icon}
                  onChange={handleChange('icon')}
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Enter icon (e.g., fa-database, fa-shield-halved, or emoji)'
                />
                <small className='text-muted mt-1 d-block'>
                  Enter FontAwesome class (e.g., fa-database) or emoji
                </small>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-semibold fs-6'>Color</label>
              <div className='col-lg-8 fv-row'>
                <div className='d-flex align-items-center gap-3'>
                  <input
                    type='color'
                    value={formData.color}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, color: e.target.value }))
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: '2px'
                    }}
                    title='Choose color'
                  />
                  <input
                    type='text'
                    value={formData.color}
                    onChange={handleChange('color')}
                    className='form-control form-control-lg form-control-solid'
                    placeholder='#e74c3c'
                    style={{ flex: 1 }}
                  />
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: formData.color,
                      border: '1px solid #ccc',
                      flexShrink: 0
                    }}
                    title={formData.color}
                  ></div>
                </div>
                <div className='mt-2'>
                  <div className='d-flex gap-2 flex-wrap'>
                    {predefinedColors.map((color) => (
                      <button
                        key={color.value}
                        type='button'
                        onClick={() => setFormData((prev) => ({ ...prev, color: color.value }))}
                        className='btn btn-sm'
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '4px',
                          backgroundColor: color.value,
                          border:
                            formData.color === color.value ? '2px solid #333' : '1px solid #ccc',
                          padding: 0,
                          cursor: 'pointer'
                        }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end gap-2'>
          <button type='button' className='btn btn-light' onClick={closeModal}>
            CANCEL
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? 'Saving...' : 'SAVE & CONTINUE'}
          </button>
        </div>
      </form>
    </div>
  )
}
